import { Link, useLocation, useMatch } from 'react-router-dom'
import { ChevronRight, Keyboard, Menu } from 'lucide-react'
import { IconButton, Tooltip } from '@/components/ui'
import ThemeToggle from '@/components/layout/ThemeToggle'
import UserMenu from '@/components/layout/UserMenu'
import { useShortcuts } from '@/components/layout/KeyboardShortcuts'
import { useSitesStore } from '@/stores/useSitesStore'

const SECTION_LABELS: Array<{ prefix: string; label: string }> = [
  { prefix: '/sites', label: 'Sites' },
  { prefix: '/keys', label: 'SSH keys' },
  { prefix: '/settings', label: 'Settings' },
  { prefix: '/admin', label: 'Admin' },
  { prefix: '/about', label: 'About' }
]

interface TopBarProps {
  navOpen: boolean
  onOpenNav: () => void
}

export default function TopBar({ navOpen, onOpenNav }: TopBarProps): React.ReactElement {
  const { pathname } = useLocation()
  const siteMatch = useMatch('/sites/:siteId')
  const siteId = siteMatch?.params.siteId ?? null
  const siteName = useSitesStore(
    (state) => state.sites.find((site) => site.id === siteId)?.installName ?? null
  )
  const { openShortcutSheet } = useShortcuts()

  const section = SECTION_LABELS.find((entry) => pathname.startsWith(entry.prefix))

  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 sm:px-4 lg:px-6">
      <IconButton
        label="Open navigation"
        variant="ghost"
        onClick={onOpenNav}
        aria-expanded={navOpen}
        aria-controls="app-nav-drawer"
        className="lg:hidden"
      >
        <Menu className="h-5 w-5" aria-hidden="true" />
      </IconButton>

      <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
        <ol className="flex min-w-0 items-center gap-1.5 text-sm">
          <li className="shrink-0">
            {siteId ? (
              <Link
                to="/sites"
                className="rounded-control font-medium text-fg-muted transition-colors hover:text-fg"
              >
                {section?.label ?? 'Argus'}
              </Link>
            ) : (
              <span aria-current="page" className="font-semibold text-fg">
                {section?.label ?? 'Argus'}
              </span>
            )}
          </li>

          {siteId && (
            <>
              <li aria-hidden="true" className="shrink-0">
                <ChevronRight className="h-3.5 w-3.5 text-fg-subtle" />
              </li>
              <li className="min-w-0">
                <span
                  aria-current="page"
                  className="block truncate font-semibold text-fg"
                  title={siteName ?? undefined}
                >
                  {siteName ?? 'Media'}
                </span>
              </li>
            </>
          )}
        </ol>
      </nav>

      {/* Hidden on the wrapper, not the button: Tooltip renders its own inline-flex span. */}
      <span className="hidden sm:inline-flex">
        <Tooltip content="Keyboard shortcuts (?)" side="bottom">
          <IconButton
            label="Keyboard shortcuts"
            variant="ghost"
            size="sm"
            onClick={openShortcutSheet}
          >
            <Keyboard className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </Tooltip>
      </span>

      <ThemeToggle />
      <UserMenu />
    </header>
  )
}

export { TopBar }
